import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { CtrlCollection } from "@yuigoto/ctrl";
import { 
  AccountControls,
  PersonControls,
  BillingControls,
  ShippingControls
} from "components/voucher/CreateWithVoucherControls";
import { ControlRenderer } from "components/renderers/ControlRenderer";
import { NextToggle } from "components/general/NextToggle";

import { UserAuthView } from "components/login/UserAuthView";
import { ExecuteRequest } from "core/glambox/GlamboxApi";
import { GlamboxEndpoints } from "core/glambox/GlamboxEndpoints";
import { SubscriberCreateRequest } from "core/types/SubscriberCreateRequest";
import { Voucher } from "core/types/Voucher";
import { PaymentMethod } from "core/types/PaymentMethod";
import { GlamboxCookieStore } from "core/glambox/GlamboxCookieStore";
import { BillingAddress } from "core/types/BillingAddress";
import { ShippingAddress } from "core/types/ShippingAddress";
import { Modal } from "components/modal/Modal";
import { GlamboxRecaptcha } from "core/glambox/GlamboxRecaptcha";

const STEP_INFORMATION          = 0;
const STEP_ACCOUNT_DATA         = 1;
const STEP_PERSON_DATA          = 2;
const STEP_BILLING_ADDRESS      = 3;
const STEP_SHIPPING_ADDRESS     = 4;
const STEP_SUBMIT_CONFIRMATION  = 5;
const STEP_SUBMIT_FINAL         = 6;
const STEP_DONUT_FOR_YOU        = 7;

class CreateWithVoucher extends Component {
  state = {
    step: 0,
    
    authorization: null,
    personId: null,
    person: null,

    loginModal: false,

    message: null,

    shippingIsBilling: true,

    city: null,
    state: null, 

    edition: null, 

    acceptTermsOfService: false,
    shouldAcceptMessage: "",

    voucher: null,

    captcha: null,
    captchaCreated: false,

    submitPending: false,
    submitMessage: "",
    submitFailure: false,
    submittedData: null
  };

  /**
   * @type {GlamboxCookieStore}
   */
  cookieHandle = null;

  /**
   * @type {CtrlCollection}
   */
  accountControls   = null;

  /**
   * @type {CtrlCollection}
   */
  personControls    = null;

  /**
   * @type {CtrlCollection}
   */
  billingControls   = null;

  /**
   * @type {CtrlCollection}
   */
  shippingControls  = null;

  /**
   * @type {BillingAddress}
   */
  billingAddress = null;

  /**
   * @type {ShippingAddress}
   */
  shippingAddress = null;

  constructor (props) {
    super(props);

    this.state = Object.assign(
      {},
      this.state,
      {
        authorization: props.authorization || null,
        personId: props.personId || null,
        person: props.person || null
      },
      {
        states: {},
        cities: {}
      }
    );

    this.cookieHandle = new GlamboxCookieStore(
      "/",
      process.env.GB_COOKIE_ROOT,
      false,
      null
    );
    
    this._resetControls();
  }

  componentDidMount () {
    this._getEdition();

    if (!GlamboxRecaptcha.INITIALIZED) {
      GlamboxRecaptcha.init(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        [
          this._recaptchaSet
        ],
        process.env.REACT_APP_RECAPTCHA_SIZE,
        "glambox-minha-recaptcha"
      );
    } else {
      GlamboxRecaptcha.addListener(this._recaptchaSet);
    }

    this._loadStateData();
  }

  componentDidUpdate (prevProps, prevState) {
    if (
      prevProps.authorization !== this.props.authorization 
        || prevProps.person !== this.props.person
    ) {
      this.setState(
        {
          step: 0, 
          authorization: this.props.authorization || null,
          personId: this.props.personId || null,
          person: this.props.person || null
        }, () => {
          this._resetControls();
          this._bootstrapper();
        }
      );
    }
  }

  // DATA LOADERS
  // --------------------------------------------------------------------
  _updateCityBilling = (cityId) => {
    cityId = parseInt(cityId);

    const billingCity = this.billingControls.get("billingCity"),
          billingStateId = this.billingControls.get("billingStateId"),
          stateId = billingStateId.value,
          cities = this.state.cities[stateId];

    let cityName;

    for (let city of cities) {
      if (city.codigoCepCidade === cityId) {
        cityName = city.nome;
      }
    }

    billingCity.value = cityName;
    this.forceUpdate();
  };

  _updateCityShipping = (cityId) => {
    cityId = parseInt(cityId);

    const shippingCity = this.shippingControls.get("shippingCity"),
          shippingStateId = this.shippingControls.get("shippingStateId"),
          stateId = shippingStateId.value,
          cities = this.state.cities[stateId];

    let cityName;

    for (let city of cities) {
      if (city.codigoCepCidade === cityId) {
        cityName = city.nome;
      }
    }

    shippingCity.value = cityName;
    this.forceUpdate();
  };

  _updateStateCitiesBilling = (stateId) => {
    this._requestStateCities(stateId, id => {
      id = parseInt(id);

      const billingCityId = this.billingControls.get("billingCityId"),
            billingState = this.billingControls.get("billingState"), 
            cities = this._citiesToOptions(id, this.state.cities);
      let stateName;

      for (let state of this.state.states) {
        if (state.codigoIbge === id) {
          stateName = state.nome;
        }
      }

      billingState.value = stateName;
      billingCityId.options = cities;
      
      this.forceUpdate();
    });
  };

  _updateStateCitiesShipping = (stateId) => {
    stateId = parseInt(stateId);
    this._requestStateCities(stateId, id => {
      const shippingCityId = this.shippingControls.get("shippingCityId"),
            shippingState = this.shippingControls.get("shippingState"), 
            cities = this._citiesToOptions(id, this.state.cities);

      let stateName;

      for (let state of this.state.states) {
        if (state.codigoIbge === id) {
          stateName = state.nome;
        }
      }

      shippingState.value = stateName;

      shippingCityId.options = cities;
      this.forceUpdate();
    });
  };

  _requestStateCities = async (stateId, callback) => {
    if (typeof this.state.cities[stateId] !== "undefined") {
      callback(stateId);
    } else {
      let response = await ExecuteRequest(
        GlamboxEndpoints.Open.listStateCities,
        {
          stateId: stateId
        },
        "GET",
        null
      );
    
      const {
        result, 
        statusCode
      } = response;
  
      if (statusCode === 200) {
        this.setState(
          {
            cities: Object.assign({}, this.state.cities, { [stateId]: result.result })
          },
          () => {
            callback(stateId);
          }
        );
      }
    }
  };

  _loadStateData = async () => {
    let response = await ExecuteRequest(
        GlamboxEndpoints.Open.listStates,
        null,
        "GET"
      );
    
    const {
      result, 
      statusCode
    } = response;

    if (statusCode === 200) {
      this.setState(
        {
          states: result
        },
        () => {
          const billingStateId = this.billingControls.get("billingStateId"),
                shippingStateId = this.shippingControls.get("shippingStateId"),
                states = this._stateToOptions(result);
          
          billingStateId.options = states;
          shippingStateId.options = states;
          this.forceUpdate();
        }
      );
    }
  };

  _stateToOptions = (stateData) => {
    let states = [];
    
    for (let n = 0; n < stateData.length; n++) {
      states.push({
        name: stateData[n].nome, 
        value: stateData[n].codigoIbge || stateData[n].codigoCep, 
      });
    }
    
    states.sort((a, b) => {
      return a.name > b.name;
    });
    
    return states;
  };

  _citiesToOptions = (stateId, citiesData) => {
    let cities = [];
    
    if (typeof citiesData[stateId] !== "undefined") {
      let citiesList = citiesData[stateId];
      
      for (let n = 0; n < citiesList.length; n++) {
        cities.push({
          name: citiesList[n].nome,
          //value: citiesList[n].codigoIbge
          value: citiesList[n].codigoCepCidade
        });
      }
    }
    
    cities.sort((a, b) => {
      return a.name > b.name;
    });
    
    return cities;
  }

  _mergeCitiesIntoState = (state, cities) => {
    const {
      codigoIbgeEstado
    } = cities[0];
    
    return Object.assign(
      {},
      state, 
      {
        [codigoIbgeEstado]: cities
      }
    );
  }

  _getCityCodeFromCepCode = (cepCode, stateId, citiesData) => {
    if (typeof citiesData[stateId] !== "undefined") {
      for (let city of citiesData[stateId]) {
        if (city.codigoCepCidade === parseInt(cepCode)) {
          return city.codigoIbge;
        }
      }
    }
    
    return null;
  }

  // USER DATA BOOTSTRAPPING
  // --------------------------------------------------------------------

  _recaptchaSet = (recaptcha) => {
    this.setState({
      captcha: recaptcha
    });
  };

  _recaptchaRenew = () => {
    GlamboxRecaptcha.execute();
  };

  _bootstrapper = () => {
    const {
      authorization,
      personId,
      person
    } = this.props;

    if (authorization && personId && person) {
      // SET ACCOUNT
      this.accountControls.setValue("email", person.person.username);
      this.accountControls.setValue("firstName", person.person.name);
      this.accountControls.setValue("lastName", person.person.lastName);
      this.accountControls.setValue("password", null);
      this.accountControls.setValue("passwordConfirmation", null);

      // SET PERSON
      let val = person.person.cpf;
      if (val !== null && typeof val !== "undefined") {
        if (val && val.length < 11) {
          while (val.length < 11) {
            val = "0" + val;
          }
        }
      }

      this.personControls.setValue("cpf", val);
      this.personControls.setValue("birthday", person.person.birthday);
      this.personControls.setValue("phone", person.person.phone);

      // SET BILLING 
      this.billingControls.setValue("billingPostalCode", person.person.postalCode);
      this.billingControls.setValue("billingState", person.person.state);
      this.billingControls.setValue("billingCity", person.person.city);
      this.billingControls.setValue("billingStreetNeighborhood", person.person.neighborhood);
      this.billingControls.setValue("billingStreet", person.person.street);
      this.billingControls.setValue("billingStreetNumber", person.person.streetNumber);
      this.billingControls.setValue("billingStreetExtension", person.person.streetExtensions);

      // SET SHIPPING
      this.shippingControls.setValue("shippingPostalCode", person.person.postalCode);
      this.shippingControls.setValue("shippingState", person.person.state);
      this.shippingControls.setValue("shippingCity", person.person.city);
      this.shippingControls.setValue("shippingStreetNeighborhood", person.person.neighborhood);
      this.shippingControls.setValue("shippingStreet", person.person.street);
      this.shippingControls.setValue("shippingStreetNumber", person.person.streetNumber);
      this.shippingControls.setValue("shippingStreetExtension", person.person.streetExtensions);

      let voucher = this.cookieHandle.get("gbVoucher");

      this.billingAddress = new BillingAddress();
      this.shippingAddress = new ShippingAddress();

      this._recaptchaRenew();
      // if (!this.state.captcha) {
      //   window.grecaptcha.render("captchaSubmit" ,{
      //     'sitekey': '6LexXVUUAAAAADrszPrFY8cb65jUvqjL05IV0D4C',
      //     //'sitekey': '6LfEoQ0UAAAAAPhcPfbSdEg7FCs3SdZsYz0aNHtv',
      //     'size': 'invisible',
      //     'callback': this._setCaptcha
      //   });
      //   window.grecaptcha.execute();
      // }

      this.setState(
        {
          voucher
        },
        () => {
          this.forceUpdate();
        }
      );
    }
  };

  _setCaptcha = (captcha) => {
    this.setState({
      captcha: captcha,
      captchaCreated: true
    });
  }

  _getEdition = () => {
    ExecuteRequest(
      GlamboxEndpoints.Open.nextEdition,
      null,
      "GET",
      null,
      null,
      (response) => {
        const {
          result,
          statusCode
        } = response;

        if (statusCode === 200) {
          this.setState({
            edition: result,
            submitPending: false,
            submitMessage: "",
            submitFailure: false
          }, () => {
            this._bootstrapper();
          });
        } else {
          this._bootstrapper();
        }
      }
    );
  };

  _copyBillingToShipping = () => {
    this.shippingControls.setValue(
      "shippingPostalCode", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingPostalCode") 
        : ""
    );
    this.shippingControls.setValue(
      "shippingState", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingState") 
        : ""
    );
    this.shippingControls.setValue(
      "shippingCity", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingCity") 
        : ""
    );
    this.shippingControls.setValue(
      "shippingStreetNeighborhood", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingStreetNeighborhood") 
        : ""
    );
    this.shippingControls.setValue(
      "shippingStreet", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingStreet") 
        : ""
    );
    this.shippingControls.setValue(
      "shippingStreetNumber", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingStreetNumber") 
        : ""
    );
    this.shippingControls.setValue(
      "shippingStreetExtension", 
      (this.state.shippingIsBilling === true) 
        ? this.billingControls.getValue("billingStreetExtension") 
        : ""
    );
  };

  _resetControls = () => {
    this.billingAddress = new BillingAddress();
    this.shippingControls = new ShippingAddress();

    this.accountControls = new CtrlCollection();
    for (let n = 0; n < AccountControls.length; n++) {
      this.accountControls.add(
        AccountControls[n]
      );
    }

    this.personControls = new CtrlCollection();
    for (let n = 0; n < PersonControls.length; n++) {
      this.personControls.add(
        PersonControls[n]
      );
    }

    this.billingControls = new CtrlCollection();
    for (let n = 0; n < BillingControls.length; n++) {
      switch (BillingControls[n].name) {
        case "billingPostalCode":
          BillingControls[n].onChange = (e, value) => {
            this._requestCepBilling(value);
          };
          break;
        case "billingStateId":
          BillingControls[n].onChange = (e, value) => {
            this._updateStateCitiesBilling(value);
          };
          break;
        case "billingCityId":
          BillingControls[n].onChange = (e, value) => {
            this._updateCityBilling(value);
          };
          break;
      }

      this.billingControls.add(
        BillingControls[n]
      );
    }

    this.shippingControls = new CtrlCollection();
    for (let n = 0; n < ShippingControls.length; n++) {
      switch (ShippingControls[n].name) {
        case "shippingPostalCode":
          ShippingControls[n].onChange = (e, value) => {
            this._requestCepShipping(value);
          };
          break;
        case "shippingStateId":
          ShippingControls[n].onChange = (e, value) => {
            this._updateStateCitiesShipping(value);
          };
          break;
        case "shippingCityId":
          ShippingControls[n].onChange = (e, value) => {
            this._updateCityShipping(value);
          };
          break;
      }

      this.shippingControls.add(
        ShippingControls[n]
      );
    }
  };

  _triggerShippingIsBilling = () => {
    this.setState({
      shippingIsBilling: !this.state.shippingIsBilling
    }, () => {
      this._copyBillingToShipping();
    });
  };

  _toggleLoginModal = () => {
    this.setState({
      loginModal: !this.state.loginModal
    });
  };

  _toggleTermsOfService = () => {
    this.setState({
      acceptTermsOfService: !this.state.acceptTermsOfService
    });
  };

  _finishExecution = () => {
    if (
      this.props.onExecutionFinish 
        && typeof this.props.onExecutionFinish === "function"
    ) {
      this.props.onExecutionFinish();
    }
  };

  // REQUESTS
  // --------------------------------------------------------------------

  _buildRequestObject = () => {
    const {
      state,
      props
    } = this;

    /**
     * @type {SubscriberCreateRequest}
     */
    let requestObject = new SubscriberCreateRequest(),
        voucherPayment = new PaymentMethod(),
        voucherObject = new Voucher();
  
    voucherObject.number = props.voucherNumber;//this.state.voucher;
    voucherObject.voucherProviderId = 1;

    voucherPayment.voucher = voucherObject;
    voucherPayment.typeMethod = 1;

    requestObject.listPaymentMethod.push(voucherPayment);

    requestObject.encryptedInviterId = "";
    requestObject.subscriptionId = 0;
    requestObject.subscriptionTypeId = 0;
    requestObject.compositionId = 0;
    requestObject.couponName = "";

    let mergedPersonAccount = Object.assign(
      {},
      this.personControls.toObject(),
      this.accountControls.toObject(),
      {
        password: "qqrsenhapranaodarerro",
        passwordConfirmation: "qqrsenhapranaodarerro"
      }
    );

    let shippingAddressObject = this.shippingAddress.toObject(),
        billingAddressObject = this.billingAddress.toObject();

    let shippingCityIbge,
        billingCityIbge,
        shippingCities = this.state.cities[shippingAddressObject.shippingStateId],
        billingCities = this.state.cities[billingAddressObject.billingStateId];
        
    for (let city of shippingCities) {
      if (city.codigoCepCidade === parseInt(shippingAddressObject.shippingStateCityId)) {
        shippingCityIbge = city.codigoIbge;
      }
    }

    for (let city of billingCities) {
      if (city.codigoCepCidade === parseInt(billingAddressObject.billingCityId)) {
        billingCityIbge = city.codigoIbge;
      }
    }

    shippingAddressObject.shippingStateCityId = shippingCityIbge;
    billingAddressObject.billingCityId = billingCityIbge;

    let mergedAddressObject = Object.assign(
      {},
      shippingAddressObject,
      billingAddressObject
    );

    for (let key of Object.keys(mergedPersonAccount)) {
      if (key === "birthday") {
        let birthDate = new Date(
          mergedPersonAccount[key].split("/").reverse().join("-")
          + " 00:00:00"
        );

        //requestObject[key] = birthDate.toISOString();
        requestObject[key] = birthDate.getTime();
      } else {
        requestObject[key] = mergedPersonAccount[key];
      }
    }

    for (let key of Object.keys(mergedAddressObject)) {
      requestObject[key] = mergedAddressObject[key];
    }

    requestObject.city = this.state.city;
    requestObject.state = this.state.state;

    requestObject.editionId = state.edition.editionId;
    requestObject.startEditionName = state.edition.theme;

    requestObject.shippingPrice = 0;

    requestObject.wants_to_be_in_mailing_list = props.person.person.newsletterOptIn;

    requestObject.captcha = state.captcha;

    delete requestObject.billingCity;
    delete requestObject.billingState;
    delete requestObject.shippingCity;
    delete requestObject.shippingCityId;
    delete requestObject.shippingState;
    
    return requestObject.toObject(true);
  };

  _executeSubmit = async () => {
    const {
      props
    } = this;

    let requestObject = this._buildRequestObject(true);

    this.setState(
      {
        submitPending: true,
        submitMessage: "",
        submitFailure: false
      },
      async () => {
        let response = await ExecuteRequest(
          GlamboxEndpoints.Subscriber.createWithVoucher,
          requestObject,
          "POST",
          {
            Authorization: props.authorization
          },
          null
        );
        
        const {
          result,
          statusCode
        } = response;

        if (statusCode === 200) {
          this.setState({
            submitPending: false,
            submitMessage: "",
            submitFailure: false,
            submittedData: result
          });
        } else {
          //alert(JSON.stringify(requestObject));
          this.setState({
            submitPending: false,
            submitMessage: "Ocorreu um erro ao criar sua assinatura. Entre em contato com a Central de Ajuda.",
            submitFailure: true
          });

          GlamboxRecaptcha.execute();
          // if (window && window.grecaptcha) {
          //   window.grecaptcha.execute();
          // }
        }
      }
    );
  };

  _requestCepBilling = (cep) => {
    ExecuteRequest(
      GlamboxEndpoints.Open.locationFromCep,
      null,
      "GET",
      null, 
      {
        cep
      },
      (response) => {
        const {
          result,
          statusCode
        } = response;

        if (statusCode === 200) {
          this.billingControls.setValue("billingState", result.estado);
          this.billingControls.setValue("billingStateId", result.codigoIbgeEstado);
          this.billingControls.setValue("billingCity", result.cidade);
          this.billingControls.setValue("billingCityId", result.codigoCepCidade);
          this.billingControls.setValue("billingStreetNeighborhood", result.bairro);
          this.billingControls.setValue("billingStreet", result.logradouro);

          this._updateStateCitiesBilling(result.codigoIbgeEstado);
        } else {
          this.billingControls.setValue("billingState", null);
          this.billingControls.setValue("billingStateId", 0);
          this.billingControls.setValue("billingCity", null);
          this.billingControls.setValue("billingCityId", 0);
          this.billingControls.setValue("billingStreetNeighborhood", null);
          this.billingControls.setValue("billingStreet", null);
        }
        
        this.forceUpdate();
      }
    );
  };

  _requestCepShipping = (cep) => {
    ExecuteRequest(
      GlamboxEndpoints.Open.locationFromCep,
      null,
      "GET",
      null, 
      {
        cep
      },
      (response) => {
        const {
          result,
          statusCode
        } = response;

        if (statusCode === 200) {
          this.shippingControls.setValue("shippingState", result.estado);
          this.shippingControls.setValue("shippingStateId", result.codigoIbgeEstado);
          this.shippingControls.setValue("shippingCity", result.cidade);
          this.shippingControls.setValue("shippingCityId", result.codigoCepCidade);
          this.shippingControls.setValue("shippingStreetNeighborhood", result.bairro);
          this.shippingControls.setValue("shippingStreet", result.logradouro);

          this._updateStateCitiesShipping(result.codigoIbgeEstado);
        } else {
          this.shippingControls.setValue("shippingState", null);
          this.shippingControls.setValue("shippingStateId", 0);
          this.shippingControls.setValue("shippingCity", null);
          this.shippingControls.setValue("shippingCityId", 0);
          this.shippingControls.setValue("shippingStreetNeighborhood", null);
          this.shippingControls.setValue("shippingStreet", null);
        }

        this.forceUpdate();
      }
    );
  };

  // NAVIGATION
  // --------------------------------------------------------------------

  _stepMoveTo = (stepNext) => {
    const { 
      state
    } = this;

    return () => {
      switch (state.step) {
        default:
          this.setState({
            step: stepNext
          });
      }
    };
  };

  _stepNext = () => {
    const { 
      state
    } = this;

    let step = (state.step < STEP_DONUT_FOR_YOU) 
      ? state.step + 1 
      : STEP_DONUT_FOR_YOU;

    if (step === STEP_BILLING_ADDRESS) {
      let billingCepControl = this.billingControls.get("billingPostalCode");

      this._requestCepBilling(billingCepControl.value.replace(/[^\d]/g, ""));
    }

    if (step === STEP_SHIPPING_ADDRESS) {
      let shippingCepControl = this.shippingControls.get("shippingPostalCode");

      this._requestCepShipping(shippingCepControl.value.replace(/[^\d]/g, ""));
    }

    if (state.shippingIsBilling === true && step === STEP_SHIPPING_ADDRESS) {
      step += 1;
    }
    
    this.setState({
      step
    });
  };

  _stepPrev = () => {
    const { 
      state
    } = this;

    let step = (state.step > STEP_INFORMATION) 
      ? state.step - 1
      : STEP_INFORMATION;

    if (
      state.shippingIsBilling === true 
        && step === STEP_SHIPPING_ADDRESS 
    ) {
      step -= 1;
    }
    
    this.setState({
      step
    });
  };

  // VERIFY
  // ----------

  /**
   * @param {Event} e
   */
  _verifyAccountForm = (e) => {
    e.preventDefault();

    if (this.accountControls.validate()) {
      this._stepNext();
    } else {
      this.forceUpdate();
    }
  };

  /**
   * @param {Event} e
   */
  _verifyPersonForm = (e) => {
    e.preventDefault();

    if (this.personControls.validate()) {
      this._stepNext();
    } else {
      this.forceUpdate();
    }
  };

  /**
   * @param {Event} e
   */
  _verifyBillingForm = (e) => {
    e.preventDefault();

    if (this.billingControls.validate()) {
      let data = this.billingControls.toObject();

      ExecuteRequest(
        GlamboxEndpoints.Open.locationFromCep,
        null,
        "GET",
        null, 
        {
          cep: data.billingPostalCode
        },
        (response) => {
          const {
            result,
            statusCode
          } = response;
          
          let formData = this.billingControls.toObject(),
              accountData = this.accountControls.toObject(), 
              personData = this.personControls.toObject();

          this.billingAddress.billingCityId = result.codigoIbgeCidade;
          this.billingAddress.billingStateId = result.codigoIbgeEstado;
          this.billingAddress.billingPhone = personData.phone;

          for (let key of Object.keys(formData)) {
            this.billingAddress[key] = formData[key];
          }

          if (this.state.shippingIsBilling === true) {
            this.shippingAddress.shippingCityId = result.codigoIbgeCidade;
            this.shippingAddress.shippingStateId = result.codigoIbgeEstado;
            this.shippingAddress.shippingPhone = personData.phone;
            this.shippingAddress.shippingName = accountData.firstName.trim() 
              + " " 
              + accountData.lastName.trim();

            for (let key of Object.keys(formData)) {
              this.shippingAddress[key.replace("billing", "shipping")] = formData[key];
            }
          }

          this.setState(
            {
              city: result.cidade,
              state: result.estado
            },
            () => {
              this._stepNext();
            }
          );
        }
      );
    } else {
      this.forceUpdate();
    }
  };

  /**
   * @param {Event} e
   */
  _verifyShippingForm = (e) => {
    e.preventDefault();

    if (this.shippingControls.validate()) {
      let data = this.shippingControls.toObject();

      ExecuteRequest(
        GlamboxEndpoints.Open.locationFromCep,
        null,
        "GET",
        null, 
        {
          cep: data.shippingPostalCode
        },
        (response) => {
          const {
            result,
            statusCode
          } = response;
          
          const formData = this.shippingControls.toObject(),
              accountData = this.accountControls.toObject(), 
              personData = this.personControls.toObject();

          this.shippingAddress.shippingCityId = result.codigoIbgeCidade;
          this.shippingAddress.shippingStateId = result.codigoIbgeEstado;
          this.shippingAddress.shippingPhone = personData.phone;
          this.shippingAddress.shippingName = accountData.firstName.trim() 
            + " " 
            + accountData.lastName.trim();

          for (let key of Object.keys(formData)) {
            this.shippingAddress[key] = formData[key];
          }

          this._stepNext();
        }
      );
    } else {
      this.forceUpdate();
    }
  };

  /**
   * @param {Event} e
   */
  _formEvent = (e) => {
    e.preventDefault();
  };

  /**
   * @param {Event} e
   */
  _verifyConfirmation = (e) => {
    e.preventDefault();

    const {
      state
    } = this;
    
    this.setState(
      {
        shouldAcceptMessage: ""
      },
      () => {
        if (state.acceptTermsOfService === true) {
          this._stepNext();
          this._executeSubmit();
        } else {
          this.setState({
            shouldAcceptMessage: "Você deve aceitar os termos e condições de uso e a política de privacidade para prosseguir."
          });
        }
      }
    );
  };

  // RENDERERS
  // --------------------------------------------------------------------

  _renderTestNavigation = () => {
    return (
      <>
        <button className={"btn btn-sm btn-info"} onClick={this._stepPrev}>
          PREV
        </button>
        <button className={"btn btn-sm btn-info"} onClick={this._stepNext}>
          NEXT
        </button>
      </>
    );
  };

  _renderIntroduction = () => {
    const {
      props,
      state
    } = this;

    if (
      props.authorization === null 
        || props.personId === null
        || props.person === null
    ) {
      return (
        <>
          <h4 className={"text-center"}>
            Efetue Login ou Registre-se
          </h4>

          <p className={"text-center lead"}>
            Você precisa efetuar login e/ou se cadastrar para realizar o resgate de voucher. É rápido, fácil e não leva nem um minutinho! <span>😉</span>
          </p>

          <p className={"text-center"}>
            Basta clicar no botão abaixo para efetuar login ou criar a sua conta.
          </p>

          <button className={"btn btn-light-lime btn-block"} onClick={this._toggleLoginModal}>
            Registrar ou Efetuar Login
          </button>

          <Modal 
            visible={state.loginModal}
            useDefaultDialog={false} 
            onCloseModal={this._toggleLoginModal}>
            
            <UserAuthView 
              onExecutionFinish={this._toggleLoginModal}/>
          </Modal>
        </>
      );
    }

    return (
      <>
        <h4 className={"text-center"}>
          Olá, GlamGirl!
        </h4>

        <p className={"text-center lead"}>Você está bem pertinho de começar a sua experiência <strong>Glambox</strong>. Precisamos apenas que confirme/complete alguns dados para que possamos efetivar sua assinatura e começar a enviar as suas caixinhas!</p>

        <button 
          className={"btn btn-light-lime btn-block"} 
          onClick={this._stepNext}>
          <strong>Começar!</strong> <span>✨</span>
        </button>
      </>
    );
  };

  _renderAccountForm = () => {
    return (
      <>
        <h4 className={"text-center"}>
          Sua Conta
        </h4>

        <form id={"voucher-account-form"} onSubmit={this._formEvent}>
          {ControlRenderer.renderCollection(this.accountControls)}

          <button 
            type={"submit"} 
            className={"btn btn-light-lime btn-block mt-2 mb-2"}
            onClick={this._verifyAccountForm}>
            <strong>Avançar</strong> <i className={"fa fa-chevron-right"}/>
          </button>

          <button 
            type={"button"} 
            className={"btn btn-light-pink btn-sm btn-block"}
            onClick={this._stepPrev}>
            <i className={"fa fa-chevron-left"}/> <strong>Voltar</strong>
          </button>
        </form>
      </>
    );
  };

  _renderPersonForm = () => {
    return (
      <>
        <h4 className={"text-center"}>
          Dados Pessoais
        </h4>

        <form id={"voucher-person-form"} onSubmit={this._formEvent}>
          {ControlRenderer.renderCollection(this.personControls)}

          <button 
            type={"submit"} 
            className={"btn btn-light-lime btn-block mt-2 mb-2"}
            onClick={this._verifyPersonForm}>
            <strong>Avançar</strong> <i className={"fa fa-chevron-right"}/>
          </button>

          <button 
            type={"button"} 
            className={"btn btn-light-pink btn-sm btn-block"}
            onClick={this._stepPrev}>
            <i className={"fa fa-chevron-left"}/> <strong>Voltar</strong>
          </button>
        </form>
      </>
    );
  };

  _renderBillingForm = () => {
    return (
      <>
        <h4 className={"text-center"}>
          Endereço de Cobrança
        </h4>

        <form id={"voucher-billing-form"} onSubmit={this._formEvent}>
          {ControlRenderer.renderCollection(this.billingControls)}

          <NextToggle 
            className={"voucher-inline-toggle"}
            childrenOutsideLabel={true}
            checked={this.state.shippingIsBilling} 
            onClick={this._triggerShippingIsBilling}>
            Endereço de entrega é o mesmo que de cobrança
          </NextToggle>

          <button 
            type={"submit"} 
            className={"btn btn-light-lime btn-block mt-2 mb-2"}
            onClick={this._verifyBillingForm}>
            <strong>Avançar</strong> <i className={"fa fa-chevron-right"}/>
          </button>

          <button 
            type={"button"} 
            className={"btn btn-light-pink btn-sm btn-block"}
            onClick={this._stepPrev}>
            <i className={"fa fa-chevron-left"}/> <strong>Voltar</strong>
          </button>
        </form>
      </>
    );
  };

  _renderShippingForm = () => {
    return (
      <>
        <h4 className={"text-center"}>
          Endereço de Entrega
        </h4>

        <form id={"voucher-shipping-form"} onSubmit={this._formEvent}>
          {ControlRenderer.renderCollection(this.shippingControls)}

          <button 
            type={"submit"} 
            className={"btn btn-light-lime btn-block mt-2 mb-2"}
            onClick={this._verifyShippingForm}>
            <strong>Avançar</strong> <i className={"fa fa-chevron-right"}/>
          </button>

          <button 
            type={"button"} 
            className={"btn btn-light-pink btn-sm btn-block"}
            onClick={this._stepPrev}>
            <i className={"fa fa-chevron-left"}/> <strong>Voltar</strong>
          </button>
        </form>
      </>
    );
  };

  _renderSubmitScreen = () => {
    let accountData = this.accountControls.toObject(),
        personData = this.personControls.toObject(),
        billingData = this.billingAddress.toObject(),
        shippingData = this.shippingAddress.toObject();

    const {
      state
    } = this;

    let confirmationMessage = (state.shouldAcceptMessage !== "") 
      ? (
        <p className={"voucher-message"}>
          {state.shouldAcceptMessage}
        </p>
      ) 
      : null;

    return (
      <>
        <h4 className={"text-center"}>
          Confirme seus Dados
        </h4>

        <div className={"voucher-confirm"}>
          <h6>Conta</h6>
          <p><strong>E-mail</strong>: {accountData.email}</p>
          <p><strong>Nome</strong>: {accountData.firstName}</p>
          <p><strong>Sobrenome</strong>: {accountData.lastName}</p>

          <hr/>

          <h6>Dados Pessoais</h6>
          <p><strong>CPF</strong>: {personData.cpf}</p>
          <p><strong>Data de Nascimento</strong>: {personData.birthday}</p>
          <p><strong>Telefone</strong>: {personData.phone}</p>

          <hr/>

          <h6>Endereço de Cobrança</h6>

          <p>{billingData.billingStreet}, {billingData.billingStreetNumber} - {billingData.billingStreetExtension}</p>
          <p>{billingData.billingStreetNeighborhood} - {billingData.billingCity}, {billingData.billingState}</p>
          <p>{billingData.billingPostalCode}</p>

          <hr/>

          <h6>Endereço de Entrega</h6>

          <p>
            {shippingData.shippingStreet}, {shippingData.shippingStreetNumber} - {shippingData.shippingStreetExtension}
          </p>
          <p>
            {shippingData.shippingStreetNeighborhood} - {shippingData.shippingCity}, {shippingData.shippingState}
          </p>
          <p>
            {shippingData.shippingPostalCode}
          </p>

          <hr/>

          <NextToggle 
            className={"voucher-inline-toggle"}
            childrenOutsideLabel={true}
            checked={state.acceptTermsOfService} 
            onClick={this._toggleTermsOfService}>
            <span>Aceito os <a href="/glampartner/termos" target="_blank"> Termos e Condições de Uso</a> e a <a href="/privacidade" target="_blank">Política de Privacidade</a></span>
          </NextToggle>
        </div>

        {confirmationMessage}

        <button 
          type={"button"} 
          className={"btn btn-light-lime btn-block mt-2 mb-2"} 
          onClick={this._verifyConfirmation}>
          <strong>Confirmar</strong> <i className={"fa fa-exclamation-circle"}/>
        </button>

        <button 
          type={"button"} 
          className={"btn btn-light-pink btn-sm btn-block"}
          onClick={this._stepPrev}>
          <i className={"fa fa-chevron-left"}/> <strong>Voltar</strong>
        </button>
      </>
    );
  };

  _renderStatusScreen = () => {
    const {
      state,
      props
    } = this;

    if (state.submitPending === true) {
      return (
        <>
          <p className={"text-center"}>
            <i className={"fa fa-spin fa-4x fa-circle-notch"}/>
          </p>
          <p className={"text-center"}>Enviando...</p>
        </>
      );
    }

    if (state.submitFailure === true) {
      return (
        <>
          <h4 className={"text-center"}>
            Não foi possível completar a sua inscrição!
          </h4>

          <p>
            {state.submitMessage}
          </p>

          <p>
            Verifique se preencheu corretamente os dados e tente novamente.
          </p>

          <button 
            type={"button"} 
            className={"btn btn-light-lime btn-block mt-2 mb-2"} 
            onClick={this._stepPrev}>
            <i className={"fa fa-chevron-left"}/> Voltar
          </button>

          <button 
            type={"button"} 
            className={"btn btn-light-pink btn-sm btn-block"}
            onClick={this._finishExecution}>
            <i className={"fa fa-times"}/> <strong>Fechar</strong>
          </button>
        </>
      );
    }

    const {
      person
    } = state.submittedData.personInfo;

    if (person.personStatus !== 4) {
      return (
        <>
          <h4 className={"text-center"}>
            Parabéns, você irá receber sua Glambox pelo Programa Glampartners!
          </h4>
  
          <p>
            Agora, você precisará preencher o seu perfil de beleza para conhecermos um pouquinho mais sobre você!
          </p>
  
          <p>
            Você também pode acompanhar o andamento da sua caixinha em Minha Glambox.
          </p>
          
          {/*
          <a 
            href={"https://minha.glambox.com.br"} 
            target={"_blank"}
            className={"btn btn-block btn-light-lime text-center mb-2"}>
            <strong>
              Ir para Minha Glambox
            </strong>
          </a>
          */}
  
          <button 
            className={"btn btn-sm btn-block btn-light-pink"}
            onClick={this._finishExecution}>
            <strong>
              Fechar
            </strong>
          </button>
        </>
      );
    }

    return (
      <>
        <h4 className={"text-center"}>
          Parabéns, você acaba de dar o seu primeiro passo para a sua experiência Glambox!
        </h4>

        <p>
          Agora, você precisará preencher o seu perfil de beleza para conhecermos um pouquinho mais sobre você!
        </p>

        <p>
          Você também pode acompanhar o andamento da sua caixinha em Minha Glambox.
        </p>

        {/*
        <a 
          href={"https://minha.glambox.com.br"} 
          target={"_blank"}
          className={"btn btn-block btn-light-lime text-center mb-2"}>
          <strong>
            Ir para Minha Glambox
          </strong>
        </a>
        */}

        <button 
          className={"btn btn-sm btn-block btn-light-pink"}
          onClick={this._finishExecution}>
          <strong>
            Fechar
          </strong>
        </button>
      </>
    );
  };

  _renderDonutScreen = () => {
    return (
      <>
        <h1>Do you DONUT, or not?</h1>
        <h1 className={"display-1"}>
          <span>🍩</span>
        </h1>
        {this._renderTestNavigation()}
      </>
    );
  };

  // 
  // --------------------------------------------------------------------

  // 
  // --------------------------------------------------------------------

  _renderer = () => {
    const {
      props,
      state
    } = this;


    switch (state.step) {
      case STEP_INFORMATION:
        return this._renderIntroduction();
      case STEP_ACCOUNT_DATA: 
        return this._renderAccountForm();
      case STEP_PERSON_DATA: 
        return this._renderPersonForm();
      case STEP_BILLING_ADDRESS: 
        return this._renderBillingForm();
      case STEP_SHIPPING_ADDRESS: 
        return this._renderShippingForm();
      case STEP_SUBMIT_CONFIRMATION: 
        return this._renderSubmitScreen();
      case STEP_SUBMIT_FINAL: 
        return this._renderStatusScreen();
      case STEP_DONUT_FOR_YOU: 
        return this._renderDonutScreen();
      default:
        return (
          <>
            <h1>Oi!</h1>
            {this._renderTestNavigation()}
          </>
        );
    }
  };

  render () {
    const {
      props,
      state
    } = this;

    return (
      <>
        {this._renderer()}

        <div id={"captchaSubmit"}/>
      </>
    );
  }
}

CreateWithVoucher.defaultProps = {
  authorization: null,
  personId: null,
  person: null,
  onExecutionFinish: null,
  voucherNumber:null
};

CreateWithVoucher.propTypes = {
  authorization: PropTypes.string,
  personId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  person: PropTypes.object,
  onExecutionFinish: PropTypes.func,
  voucherNumber: PropTypes.string
};

export {
  CreateWithVoucher
};
